import { getLogger } from '../../../libs/logger';
import { StorageKeys } from '../../../shared/constants/storageKeys';

const logger = getLogger('chunkReload');

/**
 * Whether an error is a failed dynamic-import / code-split chunk load. These
 * surface either as a `ChunkLoadError` or a "Loading chunk … failed" message.
 */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    /Loading chunk [^ ]+ failed/i.test(error.message));

export const hasAttemptedChunkReload = (): boolean =>
  window.sessionStorage.getItem(StorageKeys.ChunkReloaded) !== null;

/**
 * Attempts a single full-page reload to recover from a stale-chunk failure
 * (typically a deploy rotated chunk hashes while the tab was open). The
 * one-shot guard in `sessionStorage` prevents a reload loop.
 *
 * @returns `true` if a reload was triggered; `false` if we already reloaded
 * once (so callers can render a fallback instead of looping).
 */
export const attemptChunkReloadOnce = (error: unknown): boolean => {
  if (hasAttemptedChunkReload()) {
    logger.error('chunk load failed; reload did not help', error);
    return false;
  }
  logger.error('chunk load failed; attempting recovery', error);
  window.sessionStorage.setItem(StorageKeys.ChunkReloaded, '1');
  const url = new URL(window.location.href);
  url.searchParams.set('_chunkReload', Date.now().toString());
  window.location.replace(url.toString());
  return true;
};
